/**
 * Sign up / sign in / join by invite (screenshots/93–97). Pure reducer; the page wires it to Better Auth:
 *   - email link  → authClient.signIn.magicLink({ email, name, callbackURL })      (magicLink plugin)
 *   - 6-digit code → authClient.emailOtp.sendVerificationOtp / signIn.emailOtp      (emailOTP plugin)
 *   - passkey     → authClient.signIn.passkey()                                     (passkey plugin)
 *   - invite      → authClient.organization.acceptInvitation({ invitationId }) after sign-in
 * No passwords anywhere.
 */
import { OFFICE_DOMAIN } from "../onboarding/onboardingFlow";
import type { UserGroup } from "../nav/navConfig";

export type AuthMode = "signup" | "signin" | "invite";

export interface Invite { id: string; email: string; orgName: string; invitedBy: string; group: UserGroup; ward?: number; alderpersonName?: string; expiresAt: string }

export interface SignupState {
  mode: AuthMode;
  name: string;
  email: string;
  /** Set when the user chose "Alderperson's office" before entering an email (e.g. from a ?as=office link). */
  intendedGroup: UserGroup | null;
  invite: Invite | null;
  phase: "editing" | "sending" | "sent" | "verified" | "error";
  sentAt: number | null;            // ms, for the resend countdown
  code: string;                     // 6-digit fallback
  error: string | null;
  touched: { name: boolean; email: boolean };
}

export const RESEND_AFTER_S = 45;

export const initialSignup = (mode: AuthMode = "signup", invite: Invite | null = null, intendedGroup: UserGroup | null = null): SignupState => ({
  mode, name: "", email: invite?.email ?? "", intendedGroup: invite?.group ?? intendedGroup, invite,
  phase: "editing", sentAt: null, code: "", error: null, touched: { name: false, email: false },
});

export type SignupAction =
  | { t: "name"; name: string } | { t: "email"; email: string } | { t: "blur"; field: "name" | "email" }
  | { t: "submit" } | { t: "sent"; at: number } | { t: "failed"; error: string }
  | { t: "code"; code: string } | { t: "verified" } | { t: "changeEmail" } | { t: "asResident" } | { t: "mode"; mode: AuthMode };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Field problems, in the words the form shows under the field. */
export function problems(s: SignupState): { name?: string; email?: string } {
  const out: { name?: string; email?: string } = {};
  if (s.mode !== "signin" && !s.name.trim()) out.name = "Add your name.";
  const email = s.email.trim().toLowerCase();
  if (!EMAIL.test(email)) out.email = "Enter a valid email.";
  else if (s.intendedGroup === "office" && !email.endsWith(OFFICE_DOMAIN)) out.email = `Use your ${OFFICE_DOMAIN} email.`;
  else if (s.invite && email !== s.invite.email.toLowerCase()) out.email = `This invite is for ${s.invite.email}.`;
  return out;
}

export const canSubmit = (s: SignupState) => s.phase !== "sending" && Object.keys(problems(s)).length === 0;

/** Seconds until "Resend" is allowed again (0 = now). */
export const resendIn = (s: SignupState, now: number) => (s.sentAt == null ? 0 : Math.max(0, RESEND_AFTER_S - Math.floor((now - s.sentAt) / 1000)));

export function signupReducer(s: SignupState, a: SignupAction): SignupState {
  switch (a.t) {
    case "name": return { ...s, name: a.name.slice(0, 80) };
    case "email": return s.invite ? s : { ...s, email: a.email.trim().slice(0, 254), error: null };   // invite email is locked
    case "blur": return { ...s, touched: { ...s.touched, [a.field]: true } };
    case "submit": return canSubmit(s) ? { ...s, phase: "sending", error: null } : { ...s, touched: { name: true, email: true } };
    case "sent": return { ...s, phase: "sent", sentAt: a.at };
    case "failed": return { ...s, phase: "error", error: a.error };
    case "code": return { ...s, code: a.code.replace(/\D/g, "").slice(0, 6) };
    case "verified": return { ...s, phase: "verified" };
    case "changeEmail": return s.invite ? s : { ...s, phase: "editing", sentAt: null, code: "" };
    case "asResident": return { ...s, intendedGroup: "resident", error: null };
    // Sign in ⇄ Get a pass on the same page: keep what was typed, start the new form fresh.
    case "mode": return s.invite || s.mode === a.mode ? s : { ...s, mode: a.mode, phase: "editing", sentAt: null, code: "", error: null, touched: { name: false, email: false } };
  }
}

/** Only show an error after the field was left once (or on submit), never while typing the first time. */
export const visibleProblems = (s: SignupState) => { const p = problems(s); return { name: s.touched.name ? p.name : undefined, email: s.touched.email ? p.email : undefined }; };

/** Copy for the Check-your-inbox step: which webmail to offer, from the domain. */
export function mailApps(email: string): { label: string; href: string }[] {
  const d = email.split("@")[1]?.toLowerCase() ?? "";
  // Gmail first for Gmail addresses, with Outlook beside it (screenshots/94).
  if (d === "gmail.com" || d === "googlemail.com") return [{ label: "Open Gmail", href: "https://mail.google.com/mail/u/0/#search/from%3Achicagopolitics" }, { label: "Open Outlook", href: "https://outlook.office.com/mail/" }];
  if (/^(outlook|hotmail|live|msn)\.com$/.test(d) || d === OFFICE_DOMAIN.slice(1)) return [{ label: "Open Outlook", href: "https://outlook.office.com/mail/" }];
  if (d === "yahoo.com") return [{ label: "Open Yahoo Mail", href: "https://mail.yahoo.com/" }];
  if (d === "icloud.com" || d === "me.com") return [{ label: "Open iCloud Mail", href: "https://www.icloud.com/mail" }];
  return [{ label: "Open Gmail", href: "https://mail.google.com/" }, { label: "Open Outlook", href: "https://outlook.office.com/mail/" }];
}
